import { useMemo } from 'react';

import { FormProvider } from 'core/states';
import { useGetPartFieldConfigs } from 'parts/hooks';
import { IPart } from 'parts/types';

import { EditPartParticularsFormFields } from './EditPartParticularsFormFields';
import {
  buildDefaultValues,
  buildFieldNameMap,
  buildSchema,
  groupByDisplayPosition,
} from './utils';

interface IProps {
  part?: IPart | null;
  isOpen: boolean;
  onClose: () => void;
}

export const EditPartParticulars = ({ part, isOpen, onClose }: IProps) => {
  const requestPayload = useMemo(
    () => ({
      number: part?.number ?? '',
      manufacturer: part?.manufacturerUrlName ?? '',
    }),
    [part?.number, part?.manufacturerUrlName]
  );

  const isQueryEnabled =
    isOpen && Boolean(requestPayload.number) && Boolean(requestPayload.manufacturer);

  const {
    record: fieldConfigsRecord,
    isLoading,
    isError,
  } = useGetPartFieldConfigs(requestPayload, isQueryEnabled);

  const columns = useMemo(
    () => fieldConfigsRecord?.partFieldConfigColumns ?? [],
    [fieldConfigsRecord?.partFieldConfigColumns]
  );

  const allConfigs = useMemo(() => columns.flat(), [columns]);

  const groupedConfigsPerColumn = useMemo(() => columns.map(groupByDisplayPosition), [columns]);

  const defaultValues = useMemo(() => buildDefaultValues(allConfigs), [allConfigs]);

  const schema = useMemo(() => buildSchema(allConfigs), [allConfigs]);

  const fieldNameMap = useMemo(() => buildFieldNameMap(allConfigs), [allConfigs]);

  return (
    <FormProvider schema={schema} defaultValues={defaultValues} resetDefaultValues>
      <EditPartParticularsFormFields
        allConfigs={allConfigs}
        defaultValues={defaultValues}
        fieldNameMap={fieldNameMap}
        groupedConfigsPerColumn={groupedConfigsPerColumn}
        isError={isError}
        isLoading={isLoading}
        isOpen={isOpen}
        manufacturerId={part?.manufacturerId}
        onClose={onClose}
        partNumber={part?.number ?? ''}
      />
    </FormProvider>
  );
};
